/**
 * Atlas PM · 项目星图 · Three.js 场景管理
 *
 * 职责：
 *   1. 初始化 renderer / scene / camera / 光照 / 背景 / 网格
 *   2. 按布局数据构建节点与边（可反复 setData 重建）
 *   3. 运行渲染循环（自动旋转、呼吸浮动、逾期脉冲、悬停/聚焦缩放）
 *   4. dispose() 彻底释放 GPU 资源 + 取消 rAF + 移除事件监听
 *
 * ⚠️ React 只负责挂载/卸载 canvas；canvas 内部渲染完全由本模块驱动。
 * ⚠️ StrictMode 会二次挂载，dispose 必须干净，否则出现节点重影 / 显存泄漏。
 */

import * as THREE from 'three';
import { addBackgroundStars, addGrid, addStarLights, setupSceneFog } from './background';
import { createCenterMesh, createGlowSprite, createNodeTrail, createTaskMesh, makeLabelSprite } from './nodes';
import { buildEdges } from './edges';
import type { StarEdgeSpec } from './edges';
import { hexToNumber } from './layout';
import type { StarLayout, StarLayoutNode } from './layout';
import { attachStarInput, createViewState } from './interactions';
import type { StarViewState } from './interactions';

export interface StarSceneCallbacks {
  onHover: (node: StarLayoutNode | null, clientX: number, clientY: number) => void;
  onClick: (node: StarLayoutNode) => void;
  onDoubleClick?: (node: StarLayoutNode) => void;
}

/** 节点配色模式：状态 / 责任人 / 进度时间轴（对应左栏 4 个视角 chip） */
export type StarColorMode = 'state' | 'owner' | 'time';

/** 责任人调色板（同责任人同色，跨渲染稳定） */
const OWNER_PALETTE: readonly string[] = [
  '#5EE7FF',
  '#A77BFF',
  '#F0C060',
  '#54E0A8',
  '#4FA8FF',
  '#FF6B8A',
  '#7BD9C0',
  '#8FB2D0',
];

/** 责任人哈希 → 调色板 */
function ownerColor(name: string | null | undefined): string {
  if (!name) return '#8FB2D0';
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return OWNER_PALETTE[h % OWNER_PALETTE.length] ?? '#8FB2D0';
}

/** 进度分档着色（0 未启 / <40 起步 / <80 推进 / ≥80 收口） */
function timeColor(progressPct: number): string {
  if (progressPct <= 0) return '#8FB2D0';
  if (progressPct < 40) return '#4FA8FF';
  if (progressPct < 80) return '#5EE7FF';
  return '#54E0A8';
}

interface RuntimeNode {
  node: StarLayoutNode;
  mesh: THREE.Mesh;
  glow: THREE.Sprite;
  trail?: THREE.Mesh;
  label?: THREE.Sprite;
  phase: number;
}

const BG_COLOR = 0x02050b;
/** 任务名标签仅在任务数不超过此值时创建，避免大量 Sprite 拖慢渲染 */
const MAX_TASK_LABELS = 60;

export class StarScene {
  private readonly canvas: HTMLCanvasElement;
  private readonly callbacks: StarSceneCallbacks;
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene: THREE.Scene;
  private readonly camera: THREE.PerspectiveCamera;
  private readonly raycaster: THREE.Raycaster;

  private readonly nodeGroup = new THREE.Group();
  private readonly edgeGroup = new THREE.Group();
  private readonly clusterGroup = new THREE.Group();
  private readonly lockGroup = new THREE.Group();
  private readonly trailGroup = new THREE.Group();
  private readonly labelGroup = new THREE.Group();

  private readonly view: StarViewState = createViewState();
  private readonly meshes = new Map<string, THREE.Mesh>();
  private runtime: RuntimeNode[] = [];
  private layout: StarLayout | null = null;
  private colorMode: StarColorMode = 'state';
  /** 星轨光晕开关（原型工具栏 `星轨光晕` + `trailGroup.visible`） */
  private showTrails = true;

  private readonly clock = new THREE.Clock();
  private rafId: number | null = null;
  private running = false;
  private readonly detachInput: () => void;
  private readonly resizeObserver: ResizeObserver | null = null;
  private readonly onWindowResize: () => void;

  constructor(canvas: HTMLCanvasElement, callbacks: StarSceneCallbacks) {
    this.canvas = canvas;
    this.callbacks = callbacks;

    const width = Math.max(canvas.clientWidth, 1);
    const height = Math.max(canvas.clientHeight, 1);

    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: false,
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.setSize(width, height, false);
    this.renderer.setClearColor(BG_COLOR, 1);

    this.scene = new THREE.Scene();
    setupSceneFog(this.scene);

    this.camera = new THREE.PerspectiveCamera(48, width / height, 0.5, 3000);
    this.camera.position.set(0, 0, this.view.dist);

    this.raycaster = new THREE.Raycaster();
    this.raycaster.params.Points.threshold = 2;

    this.scene.add(
      this.edgeGroup,
      this.trailGroup,
      this.clusterGroup,
      this.lockGroup,
      this.labelGroup,
      this.nodeGroup,
    );
    addStarLights(this.scene);
    addBackgroundStars(this.scene);
    addGrid(this.scene);

    this.detachInput = attachStarInput({
      canvas: this.canvas,
      camera: this.camera,
      raycaster: this.raycaster,
      state: this.view,
      getNodes: () => this.layout?.nodes ?? [],
      getPickables: () => [...this.meshes.values()],
      onHover: (node, x, y) => this.callbacks.onHover(node, x, y),
      onClickNode: (node) => this.callbacks.onClick(node),
      onDoubleClickNode: (node) => this.callbacks.onDoubleClick?.(node),
      onBlank: () => {
        this.view.focusedId = null;
      },
      onAutoRotateChange: (on) => {
        this.view.autoRotate = on;
      },
    });

    this.onWindowResize = () => this.resize();
    window.addEventListener('resize', this.onWindowResize);
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(canvas);
    }

    this.running = true;
    this.clock.start();
    this.loop();
  }

  /* ── 数据 → 图元 ── */

  setData(layout: StarLayout, edgeSpecs: readonly StarEdgeSpec[]): void {
    this.layout = layout;
    this.clearAll();

    // 星座光晕 + 星座名标签（受「星座聚类」视角开关控制）
    for (const cluster of layout.clusters) {
      const halo = new THREE.Mesh(
        new THREE.SphereGeometry(layout.clusters.length <= 4 ? 6.4 : 5.2, 20, 20),
        new THREE.MeshBasicMaterial({
          color: 0x3d8bff,
          transparent: true,
          opacity: 0.055,
          depthWrite: false,
        }),
      );
      halo.position.copy(cluster.center);
      this.clusterGroup.add(halo);

      const label = makeLabelSprite(cluster.label, '#5b86ad', 32);
      label.position.copy(cluster.center);
      label.position.y += 8.2;
      label.scale.multiplyScalar(0.9);
      this.clusterGroup.add(label);
    }

    // 边
    const built = buildEdges(layout.nodes, edgeSpecs);
    for (const child of [...built.group.children]) this.edgeGroup.add(child);

    // 节点
    for (const node of layout.nodes) {
      const mesh = node.isCenter ? createCenterMesh() : createTaskMesh(node);
      mesh.position.copy(node.pos);
      mesh.userData.nodeId = node.id;
      mesh.userData.isNode = true;
      this.nodeGroup.add(mesh);
      this.meshes.set(node.id, mesh);

      const glow = createGlowSprite(node);
      this.trailGroup.add(glow);

      // 冻结节点挂锁（设计稿：冻结任务带锁标记）
      if (node.state === 'FROZEN' || node.state === 'ARCHIVED') {
        const lock = makeLabelSprite('🔒', '#A77BFF', 22);
        lock.position.copy(node.pos);
        lock.position.y += node.size + 2.2;
        this.lockGroup.add(lock);
      }

      let trail: THREE.Mesh | undefined;
      let label: THREE.Sprite | undefined;
      if (!node.isCenter) {
        trail = createNodeTrail(node);
        this.trailGroup.add(trail);
        if (layout.nodes.length - 1 <= MAX_TASK_LABELS) {
          label = makeLabelSprite(node.name, node.colorHex, 26);
          label.position.copy(node.pos);
          label.position.y += node.size + 1.3;
          this.labelGroup.add(label);
        }
      }

      this.runtime.push({ node, mesh, glow, trail, label, phase: Math.random() * Math.PI * 2 });
    }

    // 中心项目名标签
    const centerLabel = makeLabelSprite(layout.center.name, '#eaf6ff', 46);
    centerLabel.position.set(0, 7.6, 0);
    this.labelGroup.add(centerLabel);

    this.updateDebug(built.edges.length);
  }

  /* ── 工具栏 ── */

  setAutoRotate(on: boolean): void {
    this.view.autoRotate = on;
  }

  setShowEdges(on: boolean): void {
    this.view.showEdges = on;
  }

  setShowLabels(on: boolean): void {
    this.view.showLabels = on;
  }

  setShowClusters(on: boolean): void {
    this.view.showClusters = on;
  }

  /** 星轨光晕开关（对齐原型 `toggleTrails`） */
  setShowTrails(on: boolean): void {
    this.showTrails = on;
  }

  /** 切换节点配色模式（状态 / 责任人 / 进度时间轴） */
  setColorMode(mode: StarColorMode): void {
    this.colorMode = mode;
    for (const { node, mesh, glow, trail } of this.runtime) {
      const hex = this.colorFor(node);
      const col = hexToNumber(hex);
      (mesh.material as THREE.MeshStandardMaterial).color.setHex(col);
      (mesh.material as THREE.MeshStandardMaterial).emissive.setHex(col);
      (glow.material as THREE.SpriteMaterial).color.setHex(col);
      if (trail) (trail.material as THREE.MeshBasicMaterial).color.setHex(col);
    }
  }

  private colorFor(node: StarLayoutNode): string {
    if (node.isCenter) return node.colorHex;
    if (this.colorMode === 'owner') return ownerColor(node.task?.ownerName);
    if (this.colorMode === 'time') return timeColor(node.task?.progressPct ?? 0);
    return node.colorHex;
  }

  resetView(): void {
    this.view.rotY = 0;
    this.view.targetRotY = 0;
    this.view.rotX = -0.32;
    this.view.targetRotX = -0.32;
    this.view.dist = 62;
    this.view.targetDist = 62;
    this.view.focusedId = null;
  }

  focus(nodeId: string | null): void {
    this.view.focusedId = nodeId;
  }

  /* ── 渲染循环 ── */

  private loop = (): void => {
    if (!this.running) return;
    this.rafId = requestAnimationFrame(this.loop);

    const dt = this.clock.getDelta();
    const tt = this.clock.getElapsedTime();

    if (this.view.autoRotate) this.view.targetRotY += dt * 0.1;
    this.view.rotY += (this.view.targetRotY - this.view.rotY) * 0.09;
    this.view.rotX += (this.view.targetRotX - this.view.rotX) * 0.09;
    this.view.dist += (this.view.targetDist - this.view.dist) * 0.1;

    this.camera.position.x = Math.sin(this.view.rotY) * Math.cos(this.view.rotX) * this.view.dist;
    this.camera.position.y = Math.sin(this.view.rotX) * this.view.dist;
    this.camera.position.z = Math.cos(this.view.rotY) * Math.cos(this.view.rotX) * this.view.dist;
    this.camera.lookAt(0, 0, 0);

    for (const item of this.runtime) {
      const { node, mesh, glow, trail, label } = item;
      if (node.isCenter) {
        mesh.rotation.y += dt * 0.4;
        mesh.rotation.x += dt * 0.12;
        mesh.children.forEach((child, i) => {
          child.rotation.z += dt * (i === 0 ? 0.5 : -0.34);
        });
        continue;
      }

      // 呼吸浮动
      const f = Math.sin(tt * 0.9 + item.phase) * 0.24;
      mesh.position.y = node.home.y + f;
      glow.position.y = mesh.position.y;
      if (trail) {
        trail.position.y = mesh.position.y;
        trail.rotation.z += dt * (trail.userData.spin as number);
      }
      if (label) label.position.y = mesh.position.y + node.size + 1.3;

      // 逾期脉冲
      if ((node.task?.overdueDays ?? 0) > 0) {
        const k = 0.5 + Math.sin(tt * 3.1 + item.phase) * 0.5;
        const mat = mesh.material as THREE.MeshStandardMaterial;
        mat.emissiveIntensity = 0.5 + k * 0.95;
        glow.material.opacity = 0.16 + k * 0.3;
      }

      // 悬停 / 聚焦缩放
      const isHovered = this.view.hoveredId === node.id;
      const isFocused = this.view.focusedId === node.id;
      const target = isFocused ? 1.85 : isHovered ? 1.45 : 1;
      const s = node.size * target;
      mesh.scale.lerp(new THREE.Vector3(s, s, s), 0.16);

      /* ⭐ 节点标签显隐
       *
       * 设计稿里任务名**默认不显示** —— 星图靠位置和颜色识别，
       * 名称只在悬停/选中时出现（否则十几个名称会严重重叠）。
       *
       *   - 「显示标签」开关打开：全部显示（适合任务少的项目）
       *   - 开关关闭（默认）：只显示悬停/选中的那一个
       */
      if (label) {
        label.visible = this.view.showLabels || isHovered || isFocused;
        // 悬停/选中时标签更亮更大，突出当前节点
        if (isHovered || isFocused) {
          label.material.opacity = 1;
          label.scale.setScalar(1.15);
        } else {
          label.material.opacity = 0.72;
          label.scale.setScalar(1);
        }
      }
    }

    this.edgeGroup.visible = this.view.showEdges;
    /* labelGroup 不再整体开关 —— 改由每个标签自己判断（见上方循环），
       否则关掉开关就完全看不到名称，无法识别节点。 */
    this.labelGroup.visible = true;
    this.clusterGroup.visible = this.view.showClusters;
    this.trailGroup.visible = this.showTrails;
    this.nodeGroup.visible = true;
    this.lockGroup.visible = true;

    this.renderer.render(this.scene, this.camera);
  };

  private resize(): void {
    const width = this.canvas.clientWidth;
    const height = this.canvas.clientHeight;
    if (!width || !height) return;
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
  }

  private clearAll(): void {
    for (const group of [
      this.nodeGroup,
      this.edgeGroup,
      this.clusterGroup,
      this.lockGroup,
      this.trailGroup,
      this.labelGroup,
    ]) {
      this.clearGroup(group);
    }
    this.meshes.clear();
    this.runtime = [];
  }

  private clearGroup(group: THREE.Group): void {
    while (group.children.length) {
      const child = group.children.pop();
      if (!child) continue;
      disposeObject(child);
    }
  }

  private updateDebug(edgeCount: number): void {
    if (typeof window === 'undefined') return;
    (window as unknown as Record<string, unknown>).__STAR_DEBUG__ = {
      ready: true,
      nodeCount: Math.max((this.layout?.nodes.length ?? 0) - 1, 0),
      totalObjects: this.layout?.nodes.length ?? 0,
      clusterCount: this.layout?.clusters.length ?? 0,
      edgeCount,
    };
  }

  /** 彻底释放：rAF / 监听 / GPU 资源 */
  dispose(): void {
    this.running = false;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.detachInput();
    window.removeEventListener('resize', this.onWindowResize);
    this.resizeObserver?.disconnect();

    this.clearAll();

    this.scene.traverse((obj) => disposeObject(obj));
    this.renderer.dispose();

    if (typeof window !== 'undefined') {
      delete (window as unknown as Record<string, unknown>).__STAR_DEBUG__;
    }
  }
}

/** 释放单个对象的 geometry / material / map（供清理使用） */
function disposeObject(obj: THREE.Object3D): void {
  const mesh = obj as THREE.Mesh & { material?: THREE.Material | THREE.Material[] };
  const geometry = (mesh as { geometry?: THREE.BufferGeometry }).geometry;
  geometry?.dispose?.();

  const material = mesh.material;
  if (Array.isArray(material)) {
    for (const m of material) {
      (m as THREE.MeshBasicMaterial).map?.dispose?.();
      m.dispose();
    }
  } else if (material) {
    (material as THREE.MeshBasicMaterial).map?.dispose?.();
    material.dispose();
  }
}
